import { useGetActiveLoadingSlideQuery } from '@massilia/queries'
import { Button } from '@massilia/ui'
import { Plane, X } from 'lucide-react'
import { useEffect, useState } from 'react'

interface FlightSearchLoadingModalProps {
  destinationIataCode?: string
  onCancel?: () => void
  open: boolean
  originIataCode?: string
}

export function FlightSearchLoadingModal({
  destinationIataCode,
  onCancel,
  open,
  originIataCode,
}: FlightSearchLoadingModalProps) {
  const [showModal, setShowModal] = useState(open)

  // Reflète `open` immédiatement (sans effect : ajustement pendant le rendu,
  // cf. doc React) — l'effect ci-dessous ne gère que le timer d'auto-dismiss,
  // un vrai système externe.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    setShowModal(open)
  }

  const { data: activeSlide } = useGetActiveLoadingSlideQuery({
    iataCode: destinationIataCode,
    originIataCode,
  })

  // Auto-dismiss after 30 seconds
  useEffect(() => {
    if (!open) return

    const timer = setTimeout(() => {
      setShowModal(false)
    }, 30000)

    return () => {
      clearTimeout(timer)
    }
  }, [open])

  if (!showModal || !open) return null

  return (
    <div className="pointer-events-none fixed inset-0 z-40 flex scale-95 items-center justify-center md:scale-100">
      {/* Modal content - with pointer events enabled */}
      <div className="pointer-events-auto flex max-h-[90vh] w-full max-w-sm flex-col overflow-y-auto rounded-2xl border border-blue-100 bg-white p-8 shadow-2xl md:max-w-md">
        <div className="flex w-full flex-col items-center space-y-6 overflow-hidden px-4">
          {/* Logo Massilia */}
          <img alt="logo" className="h-12 shrink-0" src="/logos/massilia-logo.png" />

          {activeSlide ? (
            /* Active slide content */
            <>
              {/* Header text */}
              <h3 className="shrink-0 text-center text-lg font-semibold whitespace-pre-line text-gray-900">
                {activeSlide.headerText}
              </h3>

              {/* Body image */}
              <div className="flex min-h-0 items-center justify-center overflow-hidden rounded-lg">
                <img
                  alt={activeSlide.headerText}
                  className="max-h-full max-w-full object-contain"
                  src={activeSlide.bodyImageUrl}
                />
              </div>

              {/* Footer text */}
              <p className="shrink-0 text-center text-sm whitespace-pre-line text-gray-600">
                {activeSlide.footerText}
              </p>
            </>
          ) : (
            /* Fallback: original plane animation */
            <>
              {/* Aviation-themed animation */}
              <div className="relative h-32 w-64">
                {/* Animated clouds */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="cloud-1 absolute top-4 h-8 w-16 rounded-full bg-blue-100 opacity-60" />
                  <div className="cloud-2 absolute top-12 h-6 w-12 rounded-full bg-blue-100 opacity-40" />
                  <div className="cloud-3 absolute top-8 h-10 w-20 rounded-full bg-blue-100 opacity-50" />
                </div>

                {/* Animated plane */}
                <div className="plane-animation absolute top-1/2 left-0 -translate-y-1/2">
                  <Plane className="h-8 w-8 text-blue-600" />
                </div>

                {/* Dotted flight path */}
                <div className="absolute top-1/2 left-0 h-0.5 w-full border-t-2 border-dashed border-blue-300" />
              </div>

              {/* Loading text */}
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Recherche des meilleurs vols en cours...
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Nous analysons des centaines d&apos;options pour vous
                </p>
              </div>
            </>
          )}

          {/* Loading dots animation */}
          <div className="flex shrink-0 space-x-2">
            <div className="dot-pulse h-3 w-3 rounded-full bg-blue-500" />
            <div className="dot-pulse animation-delay-200 h-3 w-3 rounded-full bg-blue-500" />
            <div className="dot-pulse animation-delay-400 h-3 w-3 rounded-full bg-blue-500" />
          </div>

          {/* Cancel button */}
          {onCancel && (
            <Button onClick={onCancel} size="sm" variant="outline">
              <X className="mr-2 h-4 w-4" />
              Annuler la recherche
            </Button>
          )}
        </div>
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes planeMove {
          0% {
            left: 0;
            transform: translateY(-50%) rotate(0deg);
          }
          50% {
            transform: translateY(-50%) rotate(5deg);
          }
          100% {
            left: calc(100% - 2rem);
            transform: translateY(-50%) rotate(0deg);
          }
        }

        @keyframes cloudMove1 {
          0% {
            left: -10%;
          }
          100% {
            left: 110%;
          }
        }

        @keyframes cloudMove2 {
          0% {
            left: -15%;
          }
          100% {
            left: 115%;
          }
        }

        @keyframes cloudMove3 {
          0% {
            left: -20%;
          }
          100% {
            left: 120%;
          }
        }

        @keyframes dotPulse {
          0%, 100% {
            opacity: 0.2;
            transform: scale(0.8);
          }
          50% {
            opacity: 1;
            transform: scale(1.2);
          }
        }

        .plane-animation {
          animation: planeMove 3s ease-in-out infinite;
        }

        .cloud-1 {
          animation: cloudMove1 15s linear infinite;
        }

        .cloud-2 {
          animation: cloudMove2 20s linear infinite;
        }

        .cloud-3 {
          animation: cloudMove3 18s linear infinite;
        }

        .dot-pulse {
          animation: dotPulse 1.4s ease-in-out infinite;
        }

        .animation-delay-200 {
          animation-delay: 0.2s;
        }

        .animation-delay-400 {
          animation-delay: 0.4s;
        }
      `}</style>
    </div>
  )
}
